import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, Typography, styled } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const AccessDeniedRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "70vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

const AccessDeniedCard = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 560,
  textAlign: "center",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.app.radius.lg,
  boxShadow: theme.app.shadow.card,
  padding: theme.spacing(4),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(5),
  },
}));

const StatusBadge = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: "4px 12px",
  borderRadius: theme.app.radius.pill,
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
  color: theme.palette.error.main,
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
}));

const IconBadge = styled(Box)(({ theme }) => ({
  width: 84,
  height: 84,
  margin: "0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.error.main, 0.08),
  border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
  [theme.breakpoints.up("sm")]: {
    width: 96,
    height: 96,
  },
}));

const SupportPanel = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  textAlign: "left",
  padding: theme.spacing(2),
  borderRadius: theme.app.radius.md,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
}));

const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <AccessDeniedRoot>
      <AccessDeniedCard>
        <StatusBadge>
          <LockOutlinedIcon sx={{ fontSize: 14 }} />
          403 Error
        </StatusBadge>

        <IconBadge sx={{ mt: 3 }}>
          <LockOutlinedIcon sx={{ fontSize: 40, color: "error.main" }} />
        </IconBadge>

        <Typography
          variant="h3"
          sx={{
            mt: 3,
            fontWeight: 700,
            fontSize: { xs: "1.3rem", sm: "1.5rem" },
            color: "text.primary",
          }}
        >
          Access Denied
        </Typography>

        <Typography
          variant="body1"
          sx={{ mt: 1.5, fontWeight: 500, color: "text.primary" }}
        >
          {"You don't have permission to access this page."}
        </Typography>

        <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
          Your account does not have the required permission to view this page.
        </Typography>

        <SupportPanel sx={{ mt: 3 }}>
          <AdminPanelSettingsOutlinedIcon color="primary" />
          <Box>
            <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
              Administrator Support
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.25, color: "text.secondary" }}
            >
              Please contact your Administrator if you need access to this
              page.
            </Typography>
          </Box>
        </SupportPanel>

        <Button
          color="primary"
          variant="contained"
          size="large"
          startIcon={<DashboardOutlinedIcon />}
          onClick={() => navigate("/")}
          sx={{
            mt: 4,
            px: 3,
            minWidth: 200,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          Go to Dashboard
        </Button>
      </AccessDeniedCard>
    </AccessDeniedRoot>
  );
};

export default AccessDenied;
